import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Back from '../../components/common/Back';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import PersonFields from '../../components/sales/PersonFields';
import VisitSheet from '../../components/sales/VisitSheet';
import MailSheet from '../../components/sales/MailSheet';
import { apiRequest, apiErrorText } from '../../utils';
import './Sales.css';

/* The company, as a rep needs it thirty seconds before walking in.

   Who they are, what they have spent with us, who we know there and in what order to
   ask for them, and what has happened lately. Then the one big button.

   A company that is NOT in this unit's pipeline comes back thin from the server —
   name, town, nothing else — because a global search hit is not permission to read
   another unit's account. That case renders as an invitation to take it over. */
const money = (v) => (v == null ? '—' : `€${Math.round(v).toLocaleString()}`);

// The kinds fetch_company_timeline emits, each with its own key so nothing falls
// back to a raw database word. A kind not listed renders as itself rather than as
// a blank line — new kinds get added upstream and this must not hide them.
const TIMELINE_LABELS = {
  order: 'salesKindOrder',
  played: 'salesKindPlayed',
  award: 'salesKindAward',
  contact_change: 'salesKindContact',
  note: 'salesKindNote',
  engagement: 'salesKindEngagement',
  smartmail: 'salesKindMail',
  status_change: 'salesKindStage',
  lost_change: 'salesKindLost',
};

// A contact moment is named by the channel it happened on — "E-mail", "Bezoek",
// "Telefoon" is what a rep recognises; the word "outreach" is CRM vocabulary and
// means nothing in a doorway. Anything the server sends that is not in here still
// reads as a contact rather than as a raw database word.
const CHANNEL_LABELS = {
  email: 'salesChannelEmail',
  in_person: 'salesChannelInPerson',
  phone: 'salesChannelPhone',
  call_scheduled: 'salesChannelCall',
  whatsapp: 'salesChannelWhatsapp',
  linkedin: 'salesChannelLinkedin',
  sms: 'salesChannelSms',
  other: 'salesChannelOther',
};

const timelineLabel = (t, item) => {
  if (item.kind === 'outreach') {
    // An answer is an answer whatever carried it, and it is the one line on this
    // list a rep must not scroll past.
    return item.direction === 'in'
      ? t('salesKindReply')
      : t(CHANNEL_LABELS[item.channel] || 'salesChannelOther');
  }
  return TIMELINE_LABELS[item.kind] ? t(TIMELINE_LABELS[item.kind]) : item.kind;
};

const SalesCompany = () => {
  const { t } = useTranslation();
  const { companyId } = useParams();

  const [buId, setBuId] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVisit, setShowVisit] = useState(false);
  const [addingPerson, setAddingPerson] = useState(false);
  const [person, setPerson] = useState({ role: 'lead' });
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [mailTo, setMailTo] = useState(null);
  // The whole history, once asked for. The card ships only the last few contact
  // moments — the rest is eleven more queries, which is worth paying for when a
  // rep wants it and not on every open.
  const [allMoments, setAllMoments] = useState(null);
  const [loadingMoments, setLoadingMoments] = useState(false);
  // Its own error, not the page's: the page's is rendered inside the add-person
  // block, so a failed history load would have set a message nothing shows and
  // left the button looking like it simply did nothing.
  const [momentsError, setMomentsError] = useState(null);

  const load = useCallback(async (bu) => {
    try {
      const data = await apiRequest('GET', `/sales/company/${companyId}?bu_id=${bu}`);
      setCompany(data);
      // Anything that reloads the card has just changed the history — a visit, a
      // mail, a stage move. Drop the expanded copy rather than leaving a list on
      // screen that no longer contains what the rep did ten seconds ago.
      setAllMoments(null);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  const loadAllMoments = async () => {
    if (loadingMoments) return;
    setLoadingMoments(true);
    setMomentsError(null);
    try {
      const data = await apiRequest(
        'GET', `/sales/company/${companyId}/timeline?bu_id=${buId}`);
      // An empty history is an answer too — [] keeps the button from coming back
      // and inviting the rep to wait for the same nothing again.
      setAllMoments(data.timeline || []);
    } catch (err) {
      setMomentsError(apiErrorText(t, err, 'somethingWentWrong'));
    } finally {
      setLoadingMoments(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const ctx = await apiRequest('GET', '/sales/context');
        setBuId(ctx.business_unit_id);
        await load(ctx.business_unit_id);
      } catch {
        setError(t('salesNoAccess'));
        setLoading(false);
      }
    })();
  }, [load, t]);

  const logVisit = async (payload) => {
    const res = await apiRequest('POST', `/sales/company/${companyId}/visit`,
                                 { ...payload, bu_id: buId });
    setShowVisit(false);
    // Say what actually landed rather than a blanket "saved" — the steps behind the
    // button are independent, and a task that failed to write must not be reported
    // as one that exists.
    const done = res.done || {};
    setFlash(t('salesVisitLogged', {
      n: done.touches || 0,
      task: done.task_id ? t('salesAndFollowUp') : '',
    }));
    await load(buId);
  };

  const addPerson = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiRequest('POST', `/sales/company/${companyId}/person`,
                       { ...person, bu_id: buId });
      setAddingPerson(false);
      setPerson({ role: 'lead' });
      await load(buId);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  // Adding somebody from INSIDE the visit sheet. Deliberately does not reload the
  // company: the sheet holds a half-dictated note, and reloading would discard it.
  // The sheet renders the new person from what comes back; the page catches up
  // when the visit is saved.
  const addPersonFromSheet = async (fields) => {
    const res = await apiRequest('POST', `/sales/company/${companyId}/person`,
                                 { ...fields, bu_id: buId });
    return res.person;
  };

  const adopt = async () => {
    try {
      await apiRequest('POST', `/sales/company/${companyId}/adopt`, { bu_id: buId });
      await load(buId);
    } catch (err) {
      setError(apiErrorText(t, err, 'somethingWentWrong'));
    }
  };

  if (loading) return <Base activeNav="home"><Loading /></Base>;
  if (!company) {
    return (
      <Base activeNav="home">
        <Back to="/sales-home" />
        <Card><p className="sales-error">{error || t('somethingWentWrong')}</p></Card>
      </Base>
    );
  }

  if (!company.in_bu) {
    return (
      <Base activeNav="home">
        <Back to="/sales-home" />
        <Card>
          <h5>{company.name}</h5>
          {company.address && <p className="sales-result__sub">{company.address}</p>}
          <p className="sales-hint">{t('salesNotInPipeline')}</p>
          <Button onClick={adopt} mb="0">{t('salesAddToPipeline')}</Button>
        </Card>
      </Base>
    );
  }

  return (
    <Base activeNav="home">
      <Back to="/sales-home" />

      <Card>
        <h5 className="sales-company__name">{company.name}</h5>
        {company.address && <p className="sales-result__sub">{company.address}</p>}
        <div className="sales-pills">
          {company.status && <span className="sales-pill">{company.status.name}</span>}
          {!!company.lifetime_teams && (
            <span className="sales-pill sales-pill--soft">
              {t('salesTeamsPlayed', { n: company.lifetime_teams })}
            </span>
          )}
        </div>
        <div className="sales-stats">
          <div><b>{money(company.lifetime_revenue)}</b><span>{t('salesLifetimeSpend')}</span></div>
          <div><b>{company.lifetime_orders ?? 0}</b><span>{t('salesOrders')}</span></div>
          <div>
            <b>{company.last_order_date ? company.last_order_date.slice(0, 10) : '—'}</b>
            <span>{t('salesLastOrder')}</span>
          </div>
        </div>
        <Button mb="0" onClick={() => setShowVisit(true)}>
          <Icon name="marker" /> {t('salesIWasHere')}
        </Button>
        {flash && <p className="sales-flash">{flash}</p>}
      </Card>

      <Card>
        <div className="sales-card__head">
          <h6>{t('salesWhoWeKnow')}</h6>
          <button type="button" className="sales-mini" onClick={() => setAddingPerson((v) => !v)}>
            <Icon name="plus" /> {t('salesAddPerson')}
          </button>
        </div>

        {addingPerson && (
          <div className="sales-sub">
            <PersonFields value={person} onChange={setPerson} buId={buId} />
            {error && <p className="sales-error">{error}</p>}
            <div className="sales-actions">
              <Button theme="secondary" mb="0" styles={{ width: '40%' }}
                      onClick={() => setAddingPerson(false)}>{t('cancel')}</Button>
              {/* All three are required by the server (a CRM person without a
                  surname is not findable again), so the button waits for them
                  rather than trading a save for an error message. */}
              <Button mb="0" styles={{ width: '58%' }} onClick={addPerson}
                      disabled={saving || !person.email || !person.first_name
                                || !person.name}>
                {saving ? t('saving') : t('save')}
              </Button>
            </div>
          </div>
        )}

        {/* Already ordered by the server: the starred decision-maker first, then the
            rest of the DMU, then by lead score. That IS the answer to "who do I ask
            for at reception", so it is rendered as given. */}
        {company.contacts?.length ? company.contacts.map((c) => (
          <div key={c.id} className="sales-contact">
            <div className="sales-contact__main">
              <span className="sales-result__name">
                {c.is_primary_dmu && <span className="sales-chip__star">★</span>} {c.name}
              </span>
              {c.job_title && <span className="sales-result__sub">{c.job_title}</span>}
            </div>
            <div className="sales-contact__acts">
              {(c.mobile || c.phone) && (
                <a className="sales-act" href={`tel:${c.mobile || c.phone}`} aria-label={t('salesCall')}>
                  <Icon name="phone-call" />
                </a>
              )}
              {c.mobile && (
                <a className="sales-act sales-act--wa"
                   href={`https://wa.me/${String(c.mobile).replace(/[^0-9]/g, '')}`}
                   target="_blank" rel="noreferrer" aria-label="WhatsApp">
                  <Icon name="comment" />
                </a>
              )}
              {c.email && (
                <button type="button" className="sales-act sales-act--send"
                        onClick={() => setMailTo(c)} aria-label={t('salesSendInfo')}>
                  <Icon name="paper-plane" />
                </button>
              )}
            </div>
          </div>
        )) : <p className="sales-hint">{t('salesNoContactsYet')}</p>}
        {company.total_contacts > (company.contacts?.length || 0) && (
          <p className="sales-hint">
            {t('salesMoreContacts', {
              n: company.total_contacts - company.contacts.length,
            })}
          </p>
        )}
      </Card>

      {!!company.tasks?.length && (
        <Card>
          <h6>{t('salesOpenTasks')}</h6>
          {company.tasks.map((task) => (
            <div key={task.id} className="sales-task">
              <span>{task.title}</span>
              <span className="sales-result__sub">
                {task.due_at ? task.due_at.slice(0, 10) : ''}
              </span>
            </div>
          ))}
        </Card>
      )}

      {/* Rendered even when there is nothing to show, because the button is the
          point: an empty contact list does NOT mean an empty history, and hiding
          the card would hide the only way to find that out. */}
      <Card>
        {/* Two lists, two honest headings. The card's own is contact moments and
            says so — calling three mails "what happened before" while an order
            from this morning sits in the half we did not fetch would be a small
            lie. Once the whole history is on screen, the heading becomes true. */}
        <h6>{allMoments ? t('salesWhatHappenedBefore') : t('salesLastContact')}</h6>
        {(allMoments || company.timeline || []).map((item, i) => (
          <div key={i} className="sales-task">
            <div className="sales-task__main">
              {/* The kind is translated; the detail beside it is a proper noun
                  from the CRM (an event, a stage, a note) and stays as it is. */}
              <span>{timelineLabel(t, item)}</span>
              {item.detail && <span className="sales-result__sub">{item.detail}</span>}
            </div>
            <span className="sales-result__sub">
              {item.happened_at ? item.happened_at.slice(0, 10) : ''}
            </span>
          </div>
        ))}
        {!(allMoments || company.timeline || []).length && (
          <p className="sales-hint">
            {allMoments ? t('salesNoMomentsYet') : t('salesNoContactYet')}
          </p>
        )}
        {momentsError && <p className="sales-error">{momentsError}</p>}
        {!allMoments && (
          <button type="button" className="sales-mini sales-mini--wide"
                  onClick={loadAllMoments} disabled={loadingMoments}>
            {loadingMoments
              ? <>{t('salesLoadingMoments')}</>
              : <><Icon name="time-past" /> {t('salesAllMoments')}</>}
          </button>
        )}
      </Card>

      {mailTo && (
        <MailSheet
          company={company}
          contact={mailTo}
          buId={buId}
          onClose={() => setMailTo(null)}
          onSent={(res) => {
            setMailTo(null);
            // ★ `delivered` is false when the server is running with sending
            // switched off (GRAPH_SEND_DISABLED). Saying "sent" then is a lie the
            // rep would only discover when the customer never replies — and it is
            // the exact confusion that had a mail reported as sent that never left.
            setFlash(res.delivered === false
              ? t('salesMailNotDelivered')
              : t('salesMailSent', { address: res.sent_to }));
            load(buId);
          }}
        />
      )}

      {showVisit && (
        <VisitSheet
          company={company}
          buId={buId}
          onClose={() => setShowVisit(false)}
          onSubmit={logVisit}
          onAddPerson={addPersonFromSheet}
        />
      )}
    </Base>
  );
};

export default SalesCompany;
